'use strict';

import React, { Component } from 'react';
import Select from 'react-select';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import "@ag-grid-enterprise/all-modules/dist/styles/ag-theme-balham.css";
import { LoadingPortContext, resolvePageAccess } from '../../context/LoadingPortContext';
import { getHyphenDDMMMYYYYHHMM, getHyphenDDMMMYYYY } from '../common/utils';
var redirectURL = require('../redirectURL');

function pad(n) { return String(n).padStart(2, '0'); }
// Local getters (not toISOString) - a UTC-shift here would show "yesterday" as
// today's default in any positive-UTC-offset timezone (e.g. IST), same reasoning
// as every other date-boundary calculation in this truckload flow.
function fmtDate(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
function keyOf(transactionId, tripNo) { return transactionId + '|' + tripNo; }

// e.g. 8125000ms -> "2 hr 15 min 25 sec" (drops the hr/min parts that are
// zero, so a short trip reads as "2 min 45 sec" or just "45 sec" instead of
// "0 hr 2 min 45 sec") - same formatting convention as MobileSummary.jsx's
// own formatDuration.
function formatDuration(ms) {
    var totalSeconds = Math.round(ms / 1000);
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    var parts = [];
    if (hours > 0) parts.push(hours + ' hr');
    if (hours > 0 || minutes > 0) parts.push(minutes + ' min');
    parts.push(seconds + ' sec');
    return parts.join(' ');
}

var OUTCOME_BADGES = {
    valid: { text: "Valid", bg: "#c8e6c9", color: "#1b5e20" },
    duplicate: { text: "Duplicate", bg: "#ffe0b2", color: "#e65100" },
    wrong: { text: "Wrong Truck", bg: "#ffcdd2", color: "#c62828" },
    notfound: { text: "Not Found", bg: "#e0e0e0", color: "#424242" }
};

var TRIP_STATUS_BADGES = {
    completed: { text: "Completed", bg: "#c8e6c9", color: "#1b5e20" },
    partial: { text: "In Progress", bg: "#bbdefb", color: "#0d47a1" },
    not_started: { text: "Not Started", bg: "#e0e0e0", color: "#424242" }
};

function statusOfTrip(row) {
    if (row.loading_completed) return 'completed';
    if (row.loaded > 0 || row.loading_started) return 'partial';
    return 'not_started';
}

// Desktop dashboard over the mobile Truck Load Automation flow's own
// collections, anchored on lm_truckload_plan_data's own plan_date (the date
// range the operator picks) rather than loading_started_at/scanned_at - see
// getVehicleLoadingDashboard in lmconsigneesModel.js for why. One backend call
// returns planRows/tripStatusRows/scanRows already scoped to the same set of
// transaction_ids; everything below (the grid's own trip rows, every counter,
// the vehicle filter's own options, the View slider) is built from that one
// response, no further round-trips.
// Same lm_page_access requirement as the audit log page - a non-admin user/role
// needs a row for this screen's own URL before they can open it.
export default class LmTruckloadScanDashboard extends Component {
    static contextType = LoadingPortContext;

    constructor(props, context) {
        super(props, context);
        this._lastLoadingPortCode = context ? context.loading_port_code : null;
        var today = new Date();
        var weekAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
        this.state = {
            modules: AllModules,
            pageAccess: 0,
            loading_port_code: '',
            comp_code: '',
            lpNames: [],
            dateFrom: fmtDate(weekAgo),
            dateTo: fmtDate(today),
            vehicleFilter: '',
            // 'summary' = the existing trip-grouped grid/tiles; 'fulldata' = a
            // flat, ungrouped dump of every lm_truckload_scan_log row for the
            // same date range (+ Vehicle No filter, if one's picked) - both
            // read from the same already-fetched scanRows, no second API call.
            activeTab: 'summary',
            planRows: [],
            tripStatusRows: [],
            scanRows: [],
            tripRows: [],
            detailSlide: '',
            detailRows: [],
            detailTrip: null,
            loadshow: 'show-n',
            overly: 'show-n'
        };
    }

    componentDidMount() {
        this.pageAccess();
    }

    async pageAccess() {
        this.setState({ loadshow: 'show-m', overly: 'show-m' });
        var currentUrl = this.props.match.path;
        var resolved = await resolvePageAccess(currentUrl, this.context);
        if (!resolved) {
            this.setState({ loadshow: 'show-n', overly: 'show-n' });
            return;
        }
        this._lastLoadingPortCode = resolved.loading_port_code;
        this.setState({
            pageAccess: resolved.pageAccess,
            loading_port_code: resolved.loading_port_code,
            comp_code: resolved.comp_code,
            lpNames: resolved.lpNames,
        });
        await this.loadDashboard(resolved.loading_port_code);
        this.setState({ loadshow: 'show-n', overly: 'show-n' });
    }

    // Same reactivity as lmtruckloadauditlog.js - the navbar's Loading Port select
    // drives LoadingPortContext, and this screen refetches when it changes to a
    // port actually valid for this screen's own lpNames.
    componentDidUpdate() {
        if (this.context && this.context.loading_port_code !== this._lastLoadingPortCode) {
            this._lastLoadingPortCode = this.context.loading_port_code;
            this.onGlobalPortChange();
        }
    }

    onGlobalPortChange = () => {
        var selectedPlant = this.context.loading_port_code;
        if (!selectedPlant || !this.state.lpNames.some(each => each.loading_port_code == selectedPlant)) {
            return;
        }
        this.setState({ loadshow: 'show-m', overly: 'show-m', loading_port_code: selectedPlant, vehicleFilter: '' }, async () => {
            await this.loadDashboard(selectedPlant);
            this.setState({ loadshow: 'show-n', overly: 'show-n' });
        });
    };

    loadDashboard = (loadingPortCode) => {
        var params = {
            loading_port_code: loadingPortCode || this.state.loading_port_code,
            from: this.state.dateFrom,
            to: this.state.dateTo
        };
        console.log('[truckload] getVehicleLoadingDashboard', params);
        return redirectURL.post("/lmconsignees/truckload/getVehicleLoadingDashboard", params).then((response) => {
            var planRows = response.data.planRows || [];
            var tripStatusRows = response.data.tripStatusRows || [];
            var scanRows = response.data.scanRows || [];
            this.setState({
                planRows: planRows,
                tripStatusRows: tripStatusRows,
                scanRows: scanRows,
                tripRows: this.buildTripRows(planRows, tripStatusRows, scanRows)
            });
        }).catch((e) => {
            console.log(e);
        });
    };

    onDateChange = (field, value) => {
        this.setState({ [field]: value });
    };

    applyDateRange = () => {
        if (this.state.dateFrom > this.state.dateTo) {
            alert("'From' date can't be after 'To' date");
            return;
        }
        this.setState({ loadshow: 'show-m', overly: 'show-m' });
        this.loadDashboard().then(() => {
            this.setState({ loadshow: 'show-n', overly: 'show-n' });
        });
    };

    // One row per STARTED (transaction_id, trip_no) - a plan_data-only trip
    // (uploaded, Start Loading never tapped) has no loading activity to show
    // on this dashboard at all, so it's excluded entirely rather than showing
    // up as a mostly-blank "Not Started" row; Input Plan/the mobile flow are
    // where an operator picks up an unstarted trip, not this read-only
    // dashboard. "Started" is trip_status's own loading_started flag, not
    // just "has a trip_status doc" - a doc is upserted the moment Start
    // Loading is tapped, so the two are normally the same thing, but checking
    // the flag itself is what the field is actually for.
    buildTripRows = (planRows, tripStatusRows, scanRows) => {
        var startedKeys = new Set(
            tripStatusRows.filter((r) => r.loading_started).map((r) => keyOf(r.transaction_id, r.trip_no))
        );
        var byKey = {};
        planRows.forEach((r) => {
            var key = keyOf(r.transaction_id, r.trip_no);
            if (!startedKeys.has(key)) return;
            if (!byKey[key]) {
                byKey[key] = {
                    key: key, transaction_id: r.transaction_id, trip_no: r.trip_no, plan_id: r.plan_id,
                    trip_type: r.trip_type || null, plan_date: r.plan_date,
                    expected: 0,
                    vehicle_number: null, lsp: null, dock_number: null, scanner_name: null,
                    loading_started: false, loading_started_at: null, loading_started_by: null,
                    loading_completed: false, loading_completed_at: null, loading_completed_by: null,
                    loaded: 0, duplicate: 0, wrongTruck: 0, notFound: 0, manual: 0, auto: 0, totalScans: 0,
                    scannedHUs: {}
                };
            }
            byKey[key].expected += 1;
        });
        tripStatusRows.forEach((r) => {
            var key = keyOf(r.transaction_id, r.trip_no);
            // A trip_status row with no matching plan row in this date range
            // shouldn't happen in practice (trip_status is only ever scoped to
            // transaction_ids planRows already produced) - guarded anyway rather
            // than assumed.
            if (!byKey[key]) return;
            Object.assign(byKey[key], {
                vehicle_number: r.vehicle_number || null, lsp: r.lsp || null,
                dock_number: r.dock_number || null, scanner_name: r.scanner_name || null,
                loading_started: !!r.loading_started, loading_started_at: r.loading_started_at || null, loading_started_by: r.loading_started_by || null,
                loading_completed: !!r.loading_completed, loading_completed_at: r.loading_completed_at || null, loading_completed_by: r.loading_completed_by || null
            });
        });
        scanRows.forEach((r) => {
            var key = keyOf(r.transaction_id, r.trip_no);
            if (!byKey[key]) return;
            var entry = byKey[key];
            entry.totalScans += 1;
            if (r.is_auto) entry.auto += 1; else entry.manual += 1;
            if (r.outcome === 'duplicate') entry.duplicate += 1;
            else if (r.outcome === 'wrong') entry.wrongTruck += 1;
            else if (r.outcome === 'notfound') entry.notFound += 1;
            else if (r.outcome === 'valid' && !entry.scannedHUs[r.hu]) {
                entry.scannedHUs[r.hu] = true;
                entry.loaded += 1;
            }
        });
        return Object.values(byKey).map((e) => {
            e.remaining = Math.max(0, e.expected - e.loaded);
            e.exceptions = e.duplicate + e.wrongTruck + e.notFound;
            // Only meaningful once the trip is actually done - a still-running
            // trip's total attempt count isn't final yet, so an early ratio is
            // just noise from a tiny sample (e.g. expected 5, loaded 1 via 2
            // attempts reads as "50% efficient" despite barely having started).
            // Same rule the mobile Scan/Summary flow already applies (see
            // MobileSummary.jsx: efficiency only replaces progress once
            // completed) - this dashboard just wasn't following it yet.
            e.efficiency = e.loading_completed && e.totalScans > 0 ? Math.round((e.loaded / e.totalScans) * 100) : null;
            delete e.scannedHUs;
            return e;
        });
    };

    // planRows carries doc_no/doc_type per HU (scan_log itself never has
    // these) - looked up by transaction_id+hu, the same plan-wide-unique key
    // uploadPlanData/checkPlanDataDuplicates already enforce HU uniqueness by.
    planRowForHU = (transactionId, hu) => {
        return this.state.planRows.find((r) => r.transaction_id === transactionId && r.hu === hu);
    };

    viewTripDetail = (row) => {
        var rows = this.state.scanRows.filter((r) => keyOf(r.transaction_id, r.trip_no) === row.key)
            .slice().sort((a, b) => new Date(a.scanned_at) - new Date(b.scanned_at));
        this.setState({
            detailTrip: row,
            detailRows: rows,
            detailSlide: 'slider-translate-75p',
            overly: 'show-m'
        });
    };

    hideAll = () => {
        this.setState({ detailSlide: '', overly: 'show-n' });
    };

    onFullDataGridReady = (params) => {
        this.fullDataGridApi = params.api;
    };

    // exportDataAsCsv (ag-Grid Community, not an Enterprise-only feature - see
    // lmusers.js for the same pattern elsewhere in this app) exports exactly
    // the grid's own current rowData/columns/any in-grid column filter the
    // user has applied - never a separately recomputed dataset, so this can
    // never export something other than what's actually visible on screen.
    exportFullDataAsCsv = () => {
        if (!this.fullDataGridApi) return;
        var stamp = fmtDate(new Date());
        this.fullDataGridApi.exportDataAsCsv({
            fileName: 'truckload_scan_log_' + this.state.dateFrom + '_to_' + this.state.dateTo + '_' + stamp + '.csv'
        });
    };

    // Same slim stat-strip language as the main dashboard's own
    // renderCounterTile/renderCounterRow - label over value, accent on the
    // number itself, hairline dividers, no icon chip/card - just this
    // function's own smaller tile count, still one row.
    renderTiles = (tiles) => (
        <div style={{ display: "flex", alignItems: "stretch", flexWrap: "nowrap", overflowX: "auto", background: "#fff", border: "1px solid #e0e6ed", borderRadius: "8px" }}>
            {tiles.map((t, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", padding: "10px 14px", borderRight: i === tiles.length - 1 ? "none" : "1px solid #e8ecf1", minWidth: 0, flex: "1 1 100px", textAlign: "center" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: "#90a4ae", textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.label}</div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "6px", flexWrap: "nowrap" }}>
                        <span style={{ fontSize: "17px", fontWeight: 800, color: t.accent, lineHeight: 1.15, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{t.value}</span>
                        {t.sub ? <span style={{ fontSize: "10px", color: "#90a4ae", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.sub}</span> : null}
                    </div>
                </div>
            ))}
        </div>
    );

    // One richer tile - top accent bar, bigger number, optional progress bar -
    // used only by the main dashboard's own counters (renderCounterRow
    // below), not the slider's simpler renderTiles.
    // grow AND shrink (not the earlier grow:0) - now that the row itself is
    // nowrap (single row, always all 7 tiles), growing/shrinking equally is
    // exactly what keeps every tile the same size as its neighbors, dividing
    // the card's full width evenly instead of any one of them going oversized.
    // display:flex column + justifyContent:"space-between" (vertically, not
    // the row's own horizontal spacing) - the row already stretches every
    // tile to match its tallest neighbor's height (flexbox's own default
    // cross-axis stretch), but a plainer tile (icon+value+label only) was
    // still just top-anchored inside that shared height, leaving all its
    // slack bottom-heavy instead of spread through the tile - especially
    // visible now that "Vehicles (Pending / Completed)" (icon+value+label+sub+
    // progress bar) sits right next to plainer ones like "Total Expected
    // HUs" in the same row.
    // A thin stat strip, not a card grid - no icon tile, no colored border/
    // shadow per metric. The accent still carries meaning, just moved onto the
    // number itself (the one thing worth coloring) instead of a decorative
    // icon chip and a 4px top bar that were mostly just eating vertical space.
    // Label-over-value (not side by side) keeps this readable at a much
    // shorter height than the old card ever needed - the whole strip runs
    // ~40px tall against the old row's ~130px.
    renderCounterTile = (t, i, isLast) => (
        // First tile ("Vehicles (Pending / Completed)") gets a wider flex-basis
        // and higher flex-grow than the rest - its sub-line ("Pending X ·
        // Completed Y") runs noticeably longer than every other tile's, so it
        // was the one most often getting squeezed/ellipsized in the old equal-
        // width split.
        <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", padding: "10px 18px", borderRight: isLast ? "none" : "1px solid #e8ecf1", minWidth: 0, flex: i === 0 ? "1.6 1 210px" : "1 1 120px", textAlign: "center" }}>
            <div style={{ fontSize: "10px", fontWeight: 700, color: "#90a4ae", textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.label}</div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "7px", flexWrap: "nowrap" }}>
                <span style={{ fontSize: "18px", fontWeight: 800, color: t.accent, lineHeight: 1.15, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{t.value}</span>
                {t.sub ? <span style={{ fontSize: "10.5px", color: "#90a4ae", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.sub}</span> : null}
            </div>
        </div>
    );

    // Main dashboard counters - one slim strip (see renderCounterTile), divided
    // by hairlines instead of separate cards, so the whole thing reads as one
    // summary bar rather than a row of boxes. nowrap + overflowX:auto (not
    // flexWrap) - stays one row, shrinking to fit, only ever scrolling
    // horizontally if the viewport's too narrow to fit every metric even at
    // its smallest.
    renderCounterRow = (tiles) => (
        <div style={{ display: "flex", alignItems: "stretch", flexWrap: "nowrap", overflowX: "auto", background: "#fff", border: "1px solid #e0e6ed", borderRadius: "8px" }}>
            {tiles.map((t, i) => this.renderCounterTile(t, i, i === tiles.length - 1))}
        </div>
    );

    render() {
        // "All Vehicles" as a real, always-first option - not just relying on
        // react-select's own clear (x) icon to get back to it, which isn't
        // obvious enough once a specific vehicle's been picked (that's exactly
        // what was leaving operators stuck unable to get back to "All").
        var vehicleOptions = [{ value: '', label: 'All Vehicles' }].concat(
            [...new Set(this.state.tripRows.map((r) => r.vehicle_number).filter(Boolean))].sort()
                .map((v) => ({ value: v, label: v }))
        );
        var visibleRows = this.state.vehicleFilter
            ? this.state.tripRows.filter((r) => r.vehicle_number === this.state.vehicleFilter)
            : this.state.tripRows;

        // Full Data View's own row set - every lm_truckload_scan_log row for the
        // date range, not grouped/joined by trip like tripRows above. Same
        // vehicleFilter applied the same way (exact match on vehicle_number) so
        // "Vehicle No" means the same thing on both tabs. This is also exactly
        // what gets exported - the grid's own rowData, nothing recomputed for
        // the export separately, so "export only what's filtered" can never
        // drift from what's actually on screen.
        var visibleScanRows = this.state.vehicleFilter
            ? this.state.scanRows.filter((r) => r.vehicle_number === this.state.vehicleFilter)
            : this.state.scanRows;

        // Despite the name, this is now "started" not "planned" - tripRows
        // itself only ever contains trips with loading_started (see
        // buildTripRows) - kept as totalPlanned since every reference below
        // still reads naturally either way (count of vehicles on this
        // dashboard, whichever of the two it's counting).
        var totalPlanned = visibleRows.length;
        var loadedFull = visibleRows.filter((r) => r.loading_completed).length;
        var loadedPartial = visibleRows.filter((r) => !r.loading_completed && r.loaded > 0).length;
        var totalExpected = visibleRows.reduce((s, r) => s + r.expected, 0);
        var totalLoaded = visibleRows.reduce((s, r) => s + r.loaded, 0);
        var totalManual = visibleRows.reduce((s, r) => s + r.manual, 0);
        var totalAuto = visibleRows.reduce((s, r) => s + r.auto, 0);
        var totalAttempts = totalManual + totalAuto;
        var totalDuplicate = visibleRows.reduce((s, r) => s + r.duplicate, 0);
        var totalWrongTruck = visibleRows.reduce((s, r) => s + r.wrongTruck, 0);
        var totalNotFound = visibleRows.reduce((s, r) => s + r.notFound, 0);
        var totalExceptions = totalDuplicate + totalWrongTruck + totalNotFound;
        // Completed trips only, same reasoning as buildTripRows's own
        // per-trip efficiency gate - an in-progress trip's still-accumulating
        // scan counts aren't a final ratio yet, so mixing them in here would
        // understate/overstate the aggregate against trips that are actually
        // done. totalLoaded/totalAttempts above are deliberately left as-is
        // (every visible trip, completed or not) - those feed the raw "Total
        // Loaded HUs"/"Total Entries" tiles, which are legitimate running
        // totals regardless of completion; only the ratio itself needs
        // completed-only figures.
        var completedRows = visibleRows.filter((r) => r.loading_completed);
        var completedLoaded = completedRows.reduce((s, r) => s + r.loaded, 0);
        var completedAttempts = completedRows.reduce((s, r) => s + r.manual + r.auto, 0);
        var overallEfficiency = completedAttempts > 0 ? Math.round((completedLoaded / completedAttempts) * 100) : null;

        // Loaded-full share of what's planned, and loaded share of what's
        // expected - both drive a tile's own progress bar, purely visual (the
        // number alone is still the actual value shown).
        var loadedFullPct = totalPlanned > 0 ? Math.round((loadedFull / totalPlanned) * 100) : null;
        var loadedHuPct = totalExpected > 0 ? Math.round((totalLoaded / totalExpected) * 100) : null;
        var effAccent = overallEfficiency === null ? "#1976d2" : (overallEfficiency >= 90 ? "#2e7d32" : (overallEfficiency >= 75 ? "#f57c00" : "#c62828"));

        var counterTiles = [
            // One tile for both vehicle counts instead of two - the main value
            // is still "how many vehicles" (totalPlanned), with the
            // partial/full split folded into its own sub-line (same pattern
            // "Total Entries"/"Total Exceptions" below already use for their
            // own breakdowns) rather than a whole separate tile next to it.
            { label: "Vehicles (Pending / Completed)", value: totalPlanned, accent: "#1976d2", icon: "icofont-truck-loaded", sub: "Pending " + loadedPartial + " · Completed " + loadedFull, progressPct: loadedFullPct },
            { label: "Total Expected HUs", value: totalExpected, accent: "#1976d2", icon: "icofont-box" },
            { label: "Total Loaded HUs", value: totalLoaded, accent: "#1976d2", icon: "icofont-verification-check", progressPct: loadedHuPct },
            { label: "Total Entries", value: totalAttempts, accent: "#e65100", icon: "icofont-barcode", sub: "Manual " + totalManual + " · Auto " + totalAuto },
            { label: "Total Exceptions", value: totalExceptions, accent: "#e65100", icon: "icofont-warning-alt", sub: "Dup " + totalDuplicate + " · Wrong " + totalWrongTruck + " · N/F " + totalNotFound },
            { label: "Total Efficiency", value: overallEfficiency === null ? "—" : overallEfficiency + "%", accent: effAccent, icon: "icofont-chart-bar-graph", progressPct: overallEfficiency }
        ];

        var columnDefs = [
            {
                headerName: "", field: "actions", width: 70, pinned: "left", filter: false, sortable: false, resizable: false,
                cellRenderer: (params) => {
                    // An eye icon (icofont-eye, same icon this app already uses
                    // elsewhere for "view") - ghost style (no fill until hovered, see
                    // .vld-view-icon-btn in encss.css) reads lighter than a solid
                    // colored chip, still self-explanatory in an action column that
                    // only ever does this one thing.
                    var btn = document.createElement('button');
                    btn.type = 'button';
                    btn.title = 'View';
                    btn.className = 'vld-view-icon-btn';
                    btn.innerHTML = '<i class="icofont icofont-eye" style="font-size:15px;"></i>';
                    btn.style.cssText = "display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;background:transparent;color:#607d8b;border:none;cursor:pointer;outline:none;";
                    btn.addEventListener('click', () => this.viewTripDetail(params.data));
                    return btn;
                }
            },
            { headerName: "Transaction ID", field: "transaction_id", width: 180, filter: true, resizable: true },
            {
                headerName: "Plan Date", field: "plan_date", width: 105, filter: true, resizable: true,
                valueGetter: (p) => p.data.plan_date ? getHyphenDDMMMYYYY(p.data.plan_date) : ""
            },
            { headerName: "Trip Type", field: "trip_type", width: 115, filter: true, resizable: true, valueGetter: (p) => p.data.trip_type || "—" },
            { headerName: "Trip No", field: "trip_no", width: 95, filter: true, resizable: true },
            { headerName: "Vehicle No", field: "vehicle_number", width: 130, filter: true, resizable: true, valueGetter: (p) => p.data.vehicle_number || "—" },
            { headerName: "LSP", field: "lsp", width: 100, filter: true, resizable: true, valueGetter: (p) => p.data.lsp || "—" },
            { headerName: "Dock Number", field: "dock_number", width: 130, filter: true, resizable: true, valueGetter: (p) => p.data.dock_number || "—" },
            { headerName: "Scanner Name", field: "scanner_name", width: 135, filter: true, resizable: true, valueGetter: (p) => p.data.scanner_name || "—" },
            {
                // headerClass on the group itself colors the spanning "Loading Info"
                // header cell; headerClass+cellClass on each child colors that
                // child's own header AND every body cell in the column - together
                // the whole group (header + both columns, top to bottom) reads as
                // one blue-tinted section. See the matching vld-group-loading/
                // vld-cell-loading rules in encss.css.
                headerName: "Loading Info", headerClass: "vld-group-loading",
                children: [
                    { headerName: "Expected HUs", colId: "expected", width: 130, filter: true, resizable: true, headerClass: "vld-group-loading", cellClass: "vld-cell-loading", valueGetter: (p) => p.data.expected },
                    { headerName: "Loaded HUs", colId: "loadedHus", width: 125, filter: true, resizable: true, headerClass: "vld-group-loading", cellClass: "vld-cell-loading", valueGetter: (p) => p.data.loaded }
                ]
            },
            {
                headerName: "Scanning Summary", headerClass: "vld-group-scanning",
                children: [
                    { headerName: "Duplicate", colId: "duplicate", width: 115, filter: true, resizable: true, headerClass: "vld-group-scanning", cellClass: "vld-cell-scanning", valueGetter: (p) => p.data.duplicate },
                    { headerName: "Wrong Truck", colId: "wrongTruck", width: 130, filter: true, resizable: true, headerClass: "vld-group-scanning", cellClass: "vld-cell-scanning", valueGetter: (p) => p.data.wrongTruck },
                    { headerName: "Not Found", colId: "notFound", width: 125, filter: true, resizable: true, headerClass: "vld-group-scanning", cellClass: "vld-cell-scanning", valueGetter: (p) => p.data.notFound },
                    { headerName: "Correct", colId: "correct", width: 105, filter: true, resizable: true, headerClass: "vld-group-scanning", cellClass: "vld-cell-scanning", valueGetter: (p) => p.data.loaded },
                    { headerName: "Total Scans", colId: "totalScans", width: 130, filter: true, resizable: true, headerClass: "vld-group-scanning", cellClass: "vld-cell-scanning", valueGetter: (p) => p.data.totalScans },
                    {
                        headerName: "Efficiency", colId: "efficiency", width: 115, filter: true, resizable: true, headerClass: "vld-group-scanning", cellClass: "vld-cell-scanning",
                        valueGetter: (p) => p.data.efficiency === null ? "—" : p.data.efficiency + "%"
                    }
                ]
            },
            {
                headerName: "Status", field: "status", width: 130, filter: true, resizable: true,
                valueGetter: (p) => statusOfTrip(p.data),
                cellRenderer: (params) => {
                    var b = TRIP_STATUS_BADGES[params.value] || TRIP_STATUS_BADGES.not_started;
                    return '<span style="display:inline-block;padding:2px 10px;border-radius:12px;font-size:11.5px;font-weight:600;background:' + b.bg + ';color:' + b.color + ';">' + b.text + '</span>';
                }
            },
            {
                headerName: "Started At", field: "loading_started_at", width: 165, filter: true, resizable: true,
                valueGetter: (p) => p.data.loading_started_at ? getHyphenDDMMMYYYYHHMM(p.data.loading_started_at) : "—"
            },
            {
                headerName: "Completed At", field: "loading_completed_at", width: 165, filter: true, resizable: true,
                valueGetter: (p) => p.data.loading_completed_at ? getHyphenDDMMMYYYYHHMM(p.data.loading_completed_at) : "—"
            },
            {
                // Only meaningful once the trip has actually finished - a still-
                // running trip has no end time yet to compute a duration from.
                // loading_started_at/loading_completed_at both carry real IST
                // wall-clock digits under the UTC label (see markTripLoadingStarted/
                // Completed) - subtracting one from the other cancels that
                // mislabeling out, so plain millisecond subtraction is safe here
                // (same reasoning as MobileDashboard.jsx's own running-timer calc).
                headerName: "Total Loading Time", colId: "loadingDuration", width: 190, filter: true, resizable: true,
                valueGetter: (p) => (p.data.loading_started_at && p.data.loading_completed_at)
                    ? formatDuration(new Date(p.data.loading_completed_at) - new Date(p.data.loading_started_at))
                    : "—"
            },
            {
                // The real logged-in email behind whichever action is furthest
                // along (completed if done, else started) - the actual
                // scanned_by/loading_completed_by/loading_started_by object
                // already carries this from truckloadIdentityFromBody.
                headerName: "Logged Email", field: "logged_email", width: 195, filter: true, resizable: true,
                valueGetter: (p) => (p.data.loading_completed_by && p.data.loading_completed_by.email) || (p.data.loading_started_by && p.data.loading_started_by.email) || "—"
            }
        ];

        var detailColumnDefs = [
            { headerName: "S.No", colId: "sno", width: 70, filter: false, resizable: true, valueGetter: (p) => p.node.rowIndex + 1 },
            { headerName: "HU", field: "hu", width: 190, filter: true, resizable: true },
            { headerName: "Consignee", field: "consignee", width: 150, filter: true, resizable: true, valueGetter: (p) => p.data.consignee || "—" },
            {
                headerName: "Plan Date", field: "plan_date", width: 125, filter: true, resizable: true,
                valueGetter: (p) => p.data.plan_date ? getHyphenDDMMMYYYY(p.data.plan_date) : "—"
            },
            {
                headerName: "Doc No", field: "doc_no", width: 140, filter: true, resizable: true,
                valueGetter: (p) => {
                    var planRow = this.planRowForHU(p.data.transaction_id, p.data.hu);
                    return (planRow && planRow.doc_no) || "—";
                }
            },
            {
                headerName: "Doc Type", field: "doc_type", width: 125, filter: true, resizable: true,
                valueGetter: (p) => {
                    var planRow = this.planRowForHU(p.data.transaction_id, p.data.hu);
                    return (planRow && planRow.doc_type) || "—";
                }
            },
            { headerName: "Dock Name", field: "dock_number", width: 130, filter: true, resizable: true, valueGetter: (p) => p.data.dock_number || "—" },
            // "Entry Mode", not "Scan Type" - Auto/Manual here means how the
            // code was captured (a real scanner/camera vs. someone typing it by
            // hand - see is_auto's own classification in MobileScan.jsx), not
            // which of the two UI tabs (Scan/Manual Entry) was open at the
            // time. "Scan Type" read as asking the latter.
            { headerName: "Entry Mode", field: "is_auto", width: 130, filter: true, resizable: true, valueGetter: (p) => p.data.is_auto ? "Auto" : "Manual" },
            {
                headerName: "Result / Outcome", field: "outcome", width: 165, filter: true, resizable: true,
                cellRenderer: (params) => {
                    var b = OUTCOME_BADGES[params.value] || { text: params.value, bg: "#e0e0e0", color: "#424242" };
                    return '<span style="display:inline-block;padding:2px 10px;border-radius:12px;font-size:11.5px;font-weight:600;background:' + b.bg + ';color:' + b.color + ';">' + b.text + '</span>';
                }
            },
            {
                headerName: "Scanned At", field: "scanned_at", width: 175, filter: true, resizable: true,
                valueGetter: (p) => p.data.scanned_at ? getHyphenDDMMMYYYYHHMM(p.data.scanned_at) : ""
            },
            { headerName: "Scanner Name", field: "scanner_name", width: 145, filter: true, resizable: true, valueGetter: (p) => p.data.scanner_name || "—" }
        ];

        // Full Data View - the raw scan_log dump, so unlike detailColumnDefs
        // above (already scoped to one trip via the View slider) this needs its
        // own Transaction ID/Trip No/Vehicle No columns to actually identify
        // which trip each row belongs to.
        var fullDataColumnDefs = [
            { headerName: "S.No", colId: "sno", width: 70, filter: false, resizable: true, valueGetter: (p) => p.node.rowIndex + 1 },
            { headerName: "Transaction ID", field: "transaction_id", width: 150, filter: true, resizable: true },
            { headerName: "Trip No", field: "trip_no", width: 110, filter: true, resizable: true },
            { headerName: "Vehicle No", field: "vehicle_number", width: 130, filter: true, resizable: true, valueGetter: (p) => p.data.vehicle_number || "—" },
            { headerName: "LSP", field: "lsp", width: 110, filter: true, resizable: true, valueGetter: (p) => p.data.lsp || "—" },
            { headerName: "HU", field: "hu", width: 190, filter: true, resizable: true },
            { headerName: "Consignee", field: "consignee", width: 150, filter: true, resizable: true, valueGetter: (p) => p.data.consignee || "—" },
            {
                headerName: "Doc No", field: "doc_no", width: 140, filter: true, resizable: true,
                valueGetter: (p) => {
                    var planRow = this.planRowForHU(p.data.transaction_id, p.data.hu);
                    return (planRow && planRow.doc_no) || "—";
                }
            },
            {
                headerName: "Plan Date", field: "plan_date", width: 125, filter: true, resizable: true,
                valueGetter: (p) => p.data.plan_date ? getHyphenDDMMMYYYY(p.data.plan_date) : "—"
            },
            { headerName: "Dock Name", field: "dock_number", width: 130, filter: true, resizable: true, valueGetter: (p) => p.data.dock_number || "—" },
            { headerName: "Entry Mode", field: "is_auto", width: 130, filter: true, resizable: true, valueGetter: (p) => p.data.is_auto ? "Auto" : "Manual" },
            {
                headerName: "Result / Outcome", field: "outcome", width: 165, filter: true, resizable: true,
                cellRenderer: (params) => {
                    var b = OUTCOME_BADGES[params.value] || { text: params.value, bg: "#e0e0e0", color: "#424242" };
                    return '<span style="display:inline-block;padding:2px 10px;border-radius:12px;font-size:11.5px;font-weight:600;background:' + b.bg + ';color:' + b.color + ';">' + b.text + '</span>';
                }
            },
            {
                headerName: "Scanned At", field: "scanned_at", width: 175, filter: true, resizable: true,
                valueGetter: (p) => p.data.scanned_at ? getHyphenDDMMMYYYYHHMM(p.data.scanned_at) : ""
            },
            { headerName: "Scanner Name", field: "scanner_name", width: 145, filter: true, resizable: true, valueGetter: (p) => p.data.scanner_name || "—" },
            {
                headerName: "Scanned By", field: "scanned_by", width: 190, filter: true, resizable: true,
                valueGetter: (p) => (p.data.scanned_by && p.data.scanned_by.email) || "—"
            }
        ];

        var mainStatusBar = {
            statusPanels: [
                { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                { statusPanel: "agFilteredRowCountComponent" }
            ]
        };
        var mainSideBar = {
            toolPanels: [
                { id: "columns", labelDefault: "Columns", labelKey: "columns", iconKey: "columns", toolPanel: "agColumnsToolPanel" },
                { id: "filters", labelDefault: "Filters", labelKey: "filters", iconKey: "filter", toolPanel: "agFiltersToolPanel" }
            ]
        };

        var detailTrip = this.state.detailTrip;
        var detailTiles = detailTrip ? [
            { label: "Expected", value: detailTrip.expected, accent: "#1976d2", icon: "icofont-box" },
            { label: "Loaded", value: detailTrip.loaded, accent: "#2e7d32", icon: "icofont-check-circled" },
            { label: "Duplicate", value: detailTrip.duplicate, accent: "#e65100", icon: "icofont-warning-alt" },
            { label: "Wrong Truck", value: detailTrip.wrongTruck, accent: "#c62828", icon: "icofont-close-circled" },
            { label: "Not Found", value: detailTrip.notFound, accent: "#616161", icon: "icofont-question-circle" },
            // "Total Entries" (not "Total Scans") - same terminology the main
            // dashboard's own equivalent tile already uses for this exact value
            // (every scan attempt, valid+duplicate+wrong+notfound combined) -
            // and, same as that tile, a Manual/Auto sub-line, since the bare
            // total on its own doesn't show the split the grid's own "Entry
            // Mode" column carries per row.
            { label: "Total Entries", value: detailTrip.totalScans, accent: "#1976d2", icon: "icofont-barcode", sub: "Manual " + detailTrip.manual + " · Auto " + detailTrip.auto },
            { label: "Efficiency", value: detailTrip.efficiency === null ? "—" : detailTrip.efficiency + "%", accent: "#1976d2", icon: "icofont-chart-bar-graph" }
        ] : [];

        return (
            <div className="container-fluid" style={{ height: "100%" }}>
                <div className={"dataLoadpage " + this.state.loadshow}></div>
                <div className={"dataLoadpageimg " + this.state.loadshow}>
                    <div className="loader-box"><div className="loader-box"><div className="rotate dashed colored"></div></div></div>
                </div>
                <div className={"overlay-part " + this.state.overly} onClick={this.hideAll}></div>
                <div className="card col-xl-12" style={{ borderRadius: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                    {/* Title and filters share one row instead of stacking as two
                        separate blocks - a bordered "form" card under a title bar was
                        costing height the grid below needed far more. Compact inline
                        side-labels ("From"/"To") replace the old label-above-input
                        layout for the same reason - one row, not two, per control. */}
                    <div className="d-flex align-items-center" style={{ gap: "16px", padding: "10px 18px", flexWrap: "wrap", borderBottom: "1px solid #eef1f4" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginRight: "auto" }}>
                            <div style={{ width: "5px", height: "20px", background: "linear-gradient(180deg,#1976d2,#42a5f5)", borderRadius: "3px", flexShrink: 0 }}></div>
                            <h5 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#1a237e", whiteSpace: "nowrap" }}>Vehicle Loading Dashboard</h5>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                            <span style={{ fontSize: "11px", fontWeight: 700, color: "#90a4ae" }}>From</span>
                            <input type="date" className="vld-filter-input" style={{ width: "140px", height: "32px" }}
                                value={this.state.dateFrom} max={this.state.dateTo}
                                onChange={(e) => this.onDateChange('dateFrom', e.target.value)} />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                            <span style={{ fontSize: "11px", fontWeight: 700, color: "#90a4ae" }}>To</span>
                            <input type="date" className="vld-filter-input" style={{ width: "140px", height: "32px" }}
                                value={this.state.dateTo} min={this.state.dateFrom}
                                onChange={(e) => this.onDateChange('dateTo', e.target.value)} />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                            <span style={{ fontSize: "11px", fontWeight: 700, color: "#90a4ae" }}>Vehicle</span>
                            <div style={{ width: "180px" }}>
                                <Select
                                    // "All Vehicles" is always one of vehicleOptions itself now
                                    // (see above) - value always resolves to a real option, so
                                    // there's no unselected/placeholder state left to clear.
                                    isClearable={false}
                                    classNamePrefix="react-select"
                                    value={vehicleOptions.find((o) => o.value === this.state.vehicleFilter) || vehicleOptions[0]}
                                    onChange={(option) => this.setState({ vehicleFilter: option ? option.value : '' })}
                                    options={vehicleOptions}
                                    styles={{
                                        menu: (base) => ({ ...base, zIndex: 10 }),
                                        control: (base, state) => ({
                                            ...base, minHeight: "32px", height: "32px", fontSize: "12.5px", borderRadius: "8px",
                                            borderColor: state.isFocused ? "#1976d2" : "#cfd8dc",
                                            boxShadow: state.isFocused ? "0 0 0 2px rgba(25,118,210,0.15)" : "none"
                                        }),
                                        valueContainer: (base) => ({ ...base, padding: "0 8px" }),
                                        indicatorsContainer: (base) => ({ ...base, height: "32px" })
                                    }}
                                />
                            </div>
                        </div>
                        <button type="button" className="pill-btn pill-btn-info" style={{ height: "32px", borderRadius: "8px" }} onClick={this.applyDateRange}>
                            <i className="icofont icofont-search" style={{ marginRight: "5px" }}></i>Apply
                        </button>
                    </div>

                    <div style={{ padding: "10px 18px" }}>
                        {this.renderCounterRow(counterTiles)}
                    </div>

                    {/* Trip Summary (existing, trip-grouped) vs Full Data View (every
                        scan_log row, ungrouped) - same date/Vehicle No filters feed
                        both, just two different shapes of the same underlying data,
                        so this is a plain tab switch, not a navigation/route change.
                        Export Data shares this same line (space-between, tabs left/
                        button right) instead of its own row above the grid - only
                        shown on the Full Data View tab, since that's the only grid
                        it exports from. */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", margin: "0 18px 10px 18px" }}>
                        {/* Segmented control - one shared pill-shaped track, the active
                            side gets its own white/shadowed chip instead of an
                            underline, same "toggle" look as a two-way switch. */}
                        <div style={{ display: "inline-flex", gap: "2px", background: "#eef1f4", borderRadius: "9px", padding: "3px" }}>
                            <button
                                type="button"
                                onClick={() => this.setState({ activeTab: 'summary' })}
                                style={{
                                    padding: "7px 16px", border: "none", borderRadius: "7px", cursor: "pointer", outline: "none",
                                    fontSize: "12.5px", fontWeight: 700,
                                    background: this.state.activeTab === 'summary' ? "#fff" : "transparent",
                                    color: this.state.activeTab === 'summary' ? "#1976d2" : "#78909c",
                                    boxShadow: this.state.activeTab === 'summary' ? "0 1px 3px rgba(0,0,0,0.12)" : "none"
                                }}
                            >Trip Summary</button>
                            <button
                                type="button"
                                onClick={() => this.setState({ activeTab: 'fulldata' })}
                                style={{
                                    padding: "7px 16px", border: "none", borderRadius: "7px", cursor: "pointer", outline: "none",
                                    fontSize: "12.5px", fontWeight: 700,
                                    background: this.state.activeTab === 'fulldata' ? "#fff" : "transparent",
                                    color: this.state.activeTab === 'fulldata' ? "#1976d2" : "#78909c",
                                    boxShadow: this.state.activeTab === 'fulldata' ? "0 1px 3px rgba(0,0,0,0.12)" : "none"
                                }}
                            >Full Data View</button>
                        </div>
                        {this.state.activeTab === 'fulldata' && (
                            <button type="button" className="pill-btn pill-btn-info" style={{ height: "30px", borderRadius: "8px", outline: "none" }} onClick={this.exportFullDataAsCsv}>
                                <i className="icofont icofont-download" style={{ marginRight: "5px" }}></i>Export Data
                            </button>
                        )}
                    </div>

                    {this.state.activeTab === 'summary' ? (
                        <div className="col-xl-12 col-lg-12" style={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                            <div className="ag-theme-balham grid-wrapper vld-grid-wrapper mb-3" style={{ flex: 1, minHeight: 0 }}>
                                <AgGridReact
                                    modules={this.state.modules}
                                    columnDefs={columnDefs}
                                    rowData={visibleRows}
                                    defaultColDef={{ sortable: true, filter: true, floatingFilter: true, resizable: true }}
                                    statusBar={mainStatusBar}
                                    sideBar={mainSideBar}
                                    animateRows
                                    rowHeight={45}
                                    headerHeight={38}
                                    groupHeaderHeight={30}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="col-xl-12 col-lg-12" style={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                            <div className="ag-theme-balham grid-wrapper vld-grid-wrapper mb-3" style={{ flex: 1, minHeight: 0 }}>
                                <AgGridReact
                                    modules={this.state.modules}
                                    columnDefs={fullDataColumnDefs}
                                    rowData={visibleScanRows}
                                    defaultColDef={{ sortable: true, filter: true, floatingFilter: true, resizable: true }}
                                    statusBar={mainStatusBar}
                                    sideBar={mainSideBar}
                                    onGridReady={this.onFullDataGridReady}
                                    animateRows
                                    rowHeight={45}
                                    headerHeight={38}
                                    groupHeaderHeight={30}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* right:0 (not the base .slide-r class's own -15px) and height:100%
                    are both required here - dropping either is what was leaving this
                    panel not fully on-screen. Same fix viewLoadSummaryDetails.js's own
                    truckload sliders already needed. */}
                <div className={"slide-r " + this.state.detailSlide} style={{ overflow: "hidden", display: "flex", flexDirection: "column", height: "100%", right: 0, boxShadow: this.state.detailSlide ? undefined : "none" }}>
                    {/* Same light header language as the main dashboard's own title bar -
                        accent bar + dark text on white, not a solid gradient banner -
                        so the slider reads as part of the same screen, not a
                        different-styled panel bolted on. */}
                    <div style={{ background: "#fff", borderBottom: "1px solid #e0e6ed", padding: "14px 20px", flexShrink: 0 }}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                                <div style={{ width: "4px", height: "30px", background: "linear-gradient(180deg,#1976d2,#42a5f5)", borderRadius: "3px", flexShrink: 0 }}></div>
                                <div style={{ minWidth: 0 }}>
                                    <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1a237e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {detailTrip ? (detailTrip.vehicle_number || "Vehicle Not Captured") : ""}
                                    </h5>
                                    <p style={{ margin: 0, fontSize: "11.5px", color: "#78909c" }}>
                                        {detailTrip ? "Trip " + detailTrip.trip_no + " · " + detailTrip.transaction_id : ""}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={this.hideAll}
                                style={{ width: "28px", height: "28px", borderRadius: "50%", border: "none", background: "#f1f3f6", color: "#607d8b", cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center", outline: "none", flexShrink: 0 }}
                            >✕</button>
                        </div>
                    </div>
                    <div className="slide-r-body" style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: "16px 18px" }}>
                        <div style={{ marginBottom: "14px" }}>
                            {this.renderTiles(detailTiles)}
                        </div>
                        <div className="ag-theme-balham" style={{ width: "100%", flex: 1, minHeight: 0 }}>
                            <AgGridReact
                                modules={this.state.modules}
                                columnDefs={detailColumnDefs}
                                rowData={this.state.detailRows}
                                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                                statusBar={{
                                    statusPanels: [
                                        { statusPanel: "agTotalAndFilteredRowCountComponent", align: "right" }
                                    ]
                                }}
                                rowHeight={42}
                                headerHeight={38}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
